import React, { useMemo, useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import './OnboardingPage.css';

const steps = [
  { id: 'business', label: 'Your business' },
  { id: 'position', label: 'Current position' },
  { id: 'objectives', label: 'Objectives' },
  { id: 'review', label: 'Review' },
];

const objectiveOptions = [
  'Clarify business strategy',
  'Improve operations and workflows',
  'Increase revenue or profitability',
  'Strengthen marketing and positioning',
  'Prepare for growth or expansion',
  'Improve financial organization',
  'Build a stronger team or leadership structure',
  'Launch a new product, service, or location',
];

const supportOptions = [
  'Strategic planning',
  'Business systems and operations',
  'Financial planning and organization',
  'Marketing and brand strategy',
  'Team and leadership development',
  'Accountability and implementation support',
];

const initialFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  preferredContact: 'Email',
  businessName: '',
  role: '',
  website: '',
  location: '',
  industry: '',
  businessStage: '',
  yearsInBusiness: '',
  teamSize: '',
  revenueRange: '',
  businessDescription: '',
  currentChallenges: '',
  objectives: [],
  primaryObjective: '',
  targetTimeframe: '',
  desiredOutcomes: '',
  supportAreas: [],
  additionalDetails: '',
  referralSource: '',
  informationConsent: false,
  serviceAcknowledgement: false,
};

function FieldError({ id, children }) {
  if (!children) return null;
  return (
    <span className="onboarding-field-error" id={id} role="alert">
      {children}
    </span>
  );
}

function OnboardingPage({ onSubmit }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const progress = ((currentStep + 1) / steps.length) * 100;

  const clientName = useMemo(
    () => [formData.firstName, formData.lastName].filter(Boolean).join(' '),
    [formData.firstName, formData.lastName]
  );

  const updateField = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors((current) => ({ ...current, [name]: '' }));
    setSubmitError('');
  };

  const updateMultiSelect = (name, value) => {
    setFormData((current) => {
      const selected = current[name];
      return {
        ...current,
        [name]: selected.includes(value)
          ? selected.filter((item) => item !== value)
          : [...selected, value],
      };
    });
    setErrors((current) => ({ ...current, [name]: '' }));
  };

  const validateStep = (stepIndex) => {
    const nextErrors = {};

    if (stepIndex === 0) {
      if (!formData.firstName.trim()) nextErrors.firstName = 'Enter your first name.';
      if (!formData.lastName.trim()) nextErrors.lastName = 'Enter your last name.';
      if (!formData.email.trim()) {
        nextErrors.email = 'Enter your email address.';
      } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
        nextErrors.email = 'Enter a valid email address.';
      }
      if (!formData.businessName.trim()) nextErrors.businessName = 'Enter your business name.';
      if (!formData.role.trim()) nextErrors.role = 'Enter your role in the business.';
      if (!formData.location.trim()) nextErrors.location = 'Enter your city and state or country.';
      if (!formData.industry.trim()) nextErrors.industry = 'Enter your industry.';
    }

    if (stepIndex === 1) {
      if (!formData.businessStage) nextErrors.businessStage = 'Select your current business stage.';
      if (!formData.yearsInBusiness) nextErrors.yearsInBusiness = 'Select how long the business has operated.';
      if (!formData.teamSize) nextErrors.teamSize = 'Select your current team size.';
      if (!formData.businessDescription.trim()) {
        nextErrors.businessDescription = 'Tell us briefly what your business does.';
      }
      if (!formData.currentChallenges.trim()) {
        nextErrors.currentChallenges = 'Describe the main challenge you are facing right now.';
      }
    }

    if (stepIndex === 2) {
      if (formData.objectives.length === 0) nextErrors.objectives = 'Choose at least one objective.';
      if (!formData.primaryObjective.trim()) nextErrors.primaryObjective = 'Describe your top priority.';
      if (!formData.targetTimeframe) nextErrors.targetTimeframe = 'Select a target timeframe.';
      if (!formData.desiredOutcomes.trim()) nextErrors.desiredOutcomes = 'Describe the result you want to achieve.';
    }

    if (stepIndex === 3) {
      if (!formData.informationConsent) {
        nextErrors.informationConsent = 'Please confirm how your information may be used.';
      }
      if (!formData.serviceAcknowledgement) {
        nextErrors.serviceAcknowledgement = 'Please acknowledge the service details.';
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const goToNextStep = () => {
    if (!validateStep(currentStep)) return;
    setCurrentStep((step) => Math.min(step + 1, steps.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToPreviousStep = () => {
    setErrors({});
    setSubmitError('');
    setCurrentStep((step) => Math.max(step - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const defaultSubmit = async (payload) => {
    await addDoc(collection(db, 'consultingOnboarding'), {
      ...payload,
      submittedAt: serverTimestamp(),
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (currentStep < steps.length - 1) {
      goToNextStep();
      return;
    }

    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    setSubmitError('');

    const payload = {
      ...formData,
      service: 'Business Consulting and Strategy',
      monthlyPrice: 200,
      includedSubscription: 'Business Complete Subscription at selenne.io',
    };

    try {
      await (onSubmit || defaultSubmit)(payload);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Onboarding submission failed:', error);
      setSubmitError('We could not save your assessment. Please try again or contact Selenne Strategies.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <main className="onboarding-page onboarding-success-page">
        <section className="onboarding-success-card" aria-labelledby="onboarding-success-title">
          <div className="onboarding-success-icon" aria-hidden="true">✓</div>
          <p className="onboarding-eyebrow">Assessment received</p>
          <h1 id="onboarding-success-title">
            Thank you{clientName ? `, ${clientName}` : ''}.
          </h1>
          <p className="onboarding-success-lead">
            We will review your business, priorities, and goals to determine whether our consulting
            service is a strong fit for your needs.
          </p>

          <div className="onboarding-next-steps">
            <h2>What happens next</h2>
            <ol>
              <li>We review your business information and objectives.</li>
              <li>We contact you if we need additional information or recommend a consultation.</li>
              <li>If approved, you receive a customized agreement and account and payment instructions.</li>
            </ol>
          </div>
          <p className="onboarding-fine-print">
            Submission does not guarantee acceptance or begin service. No payment was collected here.
          </p>
        </section>
      </main>
    );
  }

  return (
    <main className="onboarding-page">
      <header className="onboarding-hero">
        <div className="onboarding-hero-content">
          <a className="onboarding-brand" href="/" aria-label="Selenne Strategies home">
            <span className="onboarding-brand-mark" aria-hidden="true">SS</span>
            <span>
              <strong>Selenne Strategies</strong>
              <small>Business Consulting &amp; Strategy</small>
            </span>
          </a>
          <p className="onboarding-eyebrow">Consulting application &amp; business assessment</p>
          <h1>Let’s understand your business—and where you want to take it.</h1>
          <p className="onboarding-hero-lead">
            Share your current position, priorities, and goals. We will use your responses to evaluate
            fit and, if approved, prepare a focused consulting plan and customized service agreement.
          </p>
        </div>
      </header>

      <div className="onboarding-layout">
        <section className="onboarding-form-card" aria-labelledby="onboarding-form-title">
          <div className="onboarding-progress-block">
            <div className="onboarding-progress-copy">
              <span>Step {currentStep + 1} of {steps.length}</span>
              <strong id="onboarding-form-title">{steps[currentStep].label}</strong>
            </div>
            <div
              className="onboarding-progress-track"
              role="progressbar"
              aria-valuemin="1"
              aria-valuemax={steps.length}
              aria-valuenow={currentStep + 1}
              aria-label={`Step ${currentStep + 1} of ${steps.length}: ${steps[currentStep].label}`}
            >
              <span style={{ width: `${progress}%` }} />
            </div>
            <ol className="onboarding-step-list" aria-label="Onboarding steps">
              {steps.map((step, index) => (
                <li
                  key={step.id}
                  className={index === currentStep ? 'is-current' : index < currentStep ? 'is-complete' : ''}
                  aria-current={index === currentStep ? 'step' : undefined}
                >
                  <span>{index < currentStep ? '✓' : index + 1}</span>
                  {step.label}
                </li>
              ))}
            </ol>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {currentStep === 0 && (
              <fieldset className="onboarding-fieldset">
                <legend>Tell us who you are</legend>
                <p className="onboarding-section-intro">
                  Start with your contact information and a few details about your business.
                </p>

                <div className="onboarding-grid onboarding-grid-two">
                  <label>
                    First name <span aria-hidden="true">*</span>
                    <input
                      name="firstName"
                      value={formData.firstName}
                      onChange={updateField}
                      autoComplete="given-name"
                      aria-invalid={Boolean(errors.firstName)}
                      aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                    />
                    <FieldError id="firstName-error">{errors.firstName}</FieldError>
                  </label>
                  <label>
                    Last name <span aria-hidden="true">*</span>
                    <input
                      name="lastName"
                      value={formData.lastName}
                      onChange={updateField}
                      autoComplete="family-name"
                      aria-invalid={Boolean(errors.lastName)}
                      aria-describedby={errors.lastName ? 'lastName-error' : undefined}
                    />
                    <FieldError id="lastName-error">{errors.lastName}</FieldError>
                  </label>
                  <label>
                    Email address <span aria-hidden="true">*</span>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={updateField}
                      autoComplete="email"
                      inputMode="email"
                      aria-invalid={Boolean(errors.email)}
                      aria-describedby={errors.email ? 'email-error' : undefined}
                    />
                    <FieldError id="email-error">{errors.email}</FieldError>
                  </label>
                  <label>
                    Phone number
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={updateField}
                      autoComplete="tel"
                      inputMode="tel"
                    />
                  </label>
                  <label>
                    Preferred contact method
                    <select name="preferredContact" value={formData.preferredContact} onChange={updateField}>
                      <option>Email</option>
                      <option>Phone</option>
                      <option>Text message</option>
                    </select>
                  </label>
                  <label>
                    Business name <span aria-hidden="true">*</span>
                    <input
                      name="businessName"
                      value={formData.businessName}
                      onChange={updateField}
                      autoComplete="organization"
                      aria-invalid={Boolean(errors.businessName)}
                      aria-describedby={errors.businessName ? 'businessName-error' : undefined}
                    />
                    <FieldError id="businessName-error">{errors.businessName}</FieldError>
                  </label>
                  <label>
                    Your role <span aria-hidden="true">*</span>
                    <input
                      name="role"
                      value={formData.role}
                      onChange={updateField}
                      placeholder="Owner, founder, executive…"
                      autoComplete="organization-title"
                      aria-invalid={Boolean(errors.role)}
                      aria-describedby={errors.role ? 'role-error' : undefined}
                    />
                    <FieldError id="role-error">{errors.role}</FieldError>
                  </label>
                  <label>
                    Website or social page
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={updateField}
                      placeholder="https://"
                      autoComplete="url"
                    />
                  </label>
                  <label>
                    City and state or country <span aria-hidden="true">*</span>
                    <input
                      name="location"
                      value={formData.location}
                      onChange={updateField}
                      autoComplete="address-level2"
                      aria-invalid={Boolean(errors.location)}
                      aria-describedby={errors.location ? 'location-error' : undefined}
                    />
                    <FieldError id="location-error">{errors.location}</FieldError>
                  </label>
                  <label>
                    Industry <span aria-hidden="true">*</span>
                    <input
                      name="industry"
                      value={formData.industry}
                      onChange={updateField}
                      placeholder="Hospitality, professional services…"
                      aria-invalid={Boolean(errors.industry)}
                      aria-describedby={errors.industry ? 'industry-error' : undefined}
                    />
                    <FieldError id="industry-error">{errors.industry}</FieldError>
                  </label>
                </div>
              </fieldset>
            )}

            {currentStep === 1 && (
              <fieldset className="onboarding-fieldset">
                <legend>Where is your business today?</legend>
                <p className="onboarding-section-intro">
                  This snapshot helps us understand your operating reality—not just the destination.
                </p>

                <div className="onboarding-grid onboarding-grid-two">
                  <label>
                    Business stage <span aria-hidden="true">*</span>
                    <select
                      name="businessStage"
                      value={formData.businessStage}
                      onChange={updateField}
                      aria-invalid={Boolean(errors.businessStage)}
                      aria-describedby={errors.businessStage ? 'businessStage-error' : undefined}
                    >
                      <option value="">Select one</option>
                      <option>Idea or pre-launch</option>
                      <option>Newly launched</option>
                      <option>Established and stabilizing</option>
                      <option>Growing or scaling</option>
                      <option>Restructuring or repositioning</option>
                    </select>
                    <FieldError id="businessStage-error">{errors.businessStage}</FieldError>
                  </label>
                  <label>
                    Time in business <span aria-hidden="true">*</span>
                    <select
                      name="yearsInBusiness"
                      value={formData.yearsInBusiness}
                      onChange={updateField}
                      aria-invalid={Boolean(errors.yearsInBusiness)}
                      aria-describedby={errors.yearsInBusiness ? 'yearsInBusiness-error' : undefined}
                    >
                      <option value="">Select one</option>
                      <option>Not launched yet</option>
                      <option>Less than 1 year</option>
                      <option>1–3 years</option>
                      <option>4–7 years</option>
                      <option>8+ years</option>
                    </select>
                    <FieldError id="yearsInBusiness-error">{errors.yearsInBusiness}</FieldError>
                  </label>
                  <label>
                    Team size <span aria-hidden="true">*</span>
                    <select
                      name="teamSize"
                      value={formData.teamSize}
                      onChange={updateField}
                      aria-invalid={Boolean(errors.teamSize)}
                      aria-describedby={errors.teamSize ? 'teamSize-error' : undefined}
                    >
                      <option value="">Select one</option>
                      <option>Just me</option>
                      <option>2–5 people</option>
                      <option>6–15 people</option>
                      <option>16–50 people</option>
                      <option>More than 50 people</option>
                    </select>
                    <FieldError id="teamSize-error">{errors.teamSize}</FieldError>
                  </label>
                  <label>
                    Approximate annual revenue
                    <select name="revenueRange" value={formData.revenueRange} onChange={updateField}>
                      <option value="">Prefer not to say</option>
                      <option>Pre-revenue</option>
                      <option>Under $50,000</option>
                      <option>$50,000–$149,999</option>
                      <option>$150,000–$499,999</option>
                      <option>$500,000–$999,999</option>
                      <option>$1 million+</option>
                    </select>
                  </label>
                </div>

                <label className="onboarding-full-field">
                  What does your business do? <span aria-hidden="true">*</span>
                  <textarea
                    name="businessDescription"
                    value={formData.businessDescription}
                    onChange={updateField}
                    rows="4"
                    placeholder="Describe your main products or services and the customers you serve."
                    aria-invalid={Boolean(errors.businessDescription)}
                    aria-describedby={errors.businessDescription ? 'businessDescription-error' : undefined}
                  />
                  <FieldError id="businessDescription-error">{errors.businessDescription}</FieldError>
                </label>

                <label className="onboarding-full-field">
                  What is the biggest challenge you are facing right now? <span aria-hidden="true">*</span>
                  <textarea
                    name="currentChallenges"
                    value={formData.currentChallenges}
                    onChange={updateField}
                    rows="5"
                    placeholder="Tell us what feels unclear, difficult, or stuck."
                    aria-invalid={Boolean(errors.currentChallenges)}
                    aria-describedby={errors.currentChallenges ? 'currentChallenges-error' : undefined}
                  />
                  <FieldError id="currentChallenges-error">{errors.currentChallenges}</FieldError>
                </label>
              </fieldset>
            )}

            {currentStep === 2 && (
              <fieldset className="onboarding-fieldset">
                <legend>What would you like to accomplish?</legend>
                <p className="onboarding-section-intro">
                  Select everything that applies, then tell us which result matters most.
                </p>

                <div className="onboarding-choice-group" aria-describedby={errors.objectives ? 'objectives-error' : undefined}>
                  <span className="onboarding-label-text">
                    Business objectives <span aria-hidden="true">*</span>
                  </span>
                  <div className="onboarding-choice-grid">
                    {objectiveOptions.map((option) => (
                      <label className="onboarding-choice" key={option}>
                        <input
                          type="checkbox"
                          checked={formData.objectives.includes(option)}
                          onChange={() => updateMultiSelect('objectives', option)}
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                  <FieldError id="objectives-error">{errors.objectives}</FieldError>
                </div>

                <label className="onboarding-full-field">
                  What is your top priority? <span aria-hidden="true">*</span>
                  <textarea
                    name="primaryObjective"
                    value={formData.primaryObjective}
                    onChange={updateField}
                    rows="4"
                    placeholder="If we could help you make meaningful progress on one thing first, what should it be?"
                    aria-invalid={Boolean(errors.primaryObjective)}
                    aria-describedby={errors.primaryObjective ? 'primaryObjective-error' : undefined}
                  />
                  <FieldError id="primaryObjective-error">{errors.primaryObjective}</FieldError>
                </label>

                <div className="onboarding-grid onboarding-grid-two">
                  <label>
                    Target timeframe <span aria-hidden="true">*</span>
                    <select
                      name="targetTimeframe"
                      value={formData.targetTimeframe}
                      onChange={updateField}
                      aria-invalid={Boolean(errors.targetTimeframe)}
                      aria-describedby={errors.targetTimeframe ? 'targetTimeframe-error' : undefined}
                    >
                      <option value="">Select one</option>
                      <option>Within 30 days</option>
                      <option>Within 90 days</option>
                      <option>Within 6 months</option>
                      <option>Within 12 months</option>
                      <option>Ongoing improvement</option>
                    </select>
                    <FieldError id="targetTimeframe-error">{errors.targetTimeframe}</FieldError>
                  </label>
                  <label>
                    How did you hear about us?
                    <select name="referralSource" value={formData.referralSource} onChange={updateField}>
                      <option value="">Select one</option>
                      <option>Referral</option>
                      <option>Search engine</option>
                      <option>Social media</option>
                      <option>Event or networking</option>
                      <option>Selenne.io</option>
                      <option>Other</option>
                    </select>
                  </label>
                </div>

                <label className="onboarding-full-field">
                  What would a successful outcome look like? <span aria-hidden="true">*</span>
                  <textarea
                    name="desiredOutcomes"
                    value={formData.desiredOutcomes}
                    onChange={updateField}
                    rows="5"
                    placeholder="Describe the practical change you want to see in your business."
                    aria-invalid={Boolean(errors.desiredOutcomes)}
                    aria-describedby={errors.desiredOutcomes ? 'desiredOutcomes-error' : undefined}
                  />
                  <FieldError id="desiredOutcomes-error">{errors.desiredOutcomes}</FieldError>
                </label>

                <div className="onboarding-choice-group">
                  <span className="onboarding-label-text">Where would support be most valuable?</span>
                  <div className="onboarding-choice-grid">
                    {supportOptions.map((option) => (
                      <label className="onboarding-choice" key={option}>
                        <input
                          type="checkbox"
                          checked={formData.supportAreas.includes(option)}
                          onChange={() => updateMultiSelect('supportAreas', option)}
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <label className="onboarding-full-field">
                  Anything else we should know?
                  <textarea
                    name="additionalDetails"
                    value={formData.additionalDetails}
                    onChange={updateField}
                    rows="4"
                    placeholder="Share timing considerations, upcoming milestones, or other useful context."
                  />
                </label>
              </fieldset>
            )}

            {currentStep === 3 && (
              <fieldset className="onboarding-fieldset">
                <legend>Review your assessment</legend>
                <p className="onboarding-section-intro">
                  Confirm the details below. You can go back to make changes before submitting.
                </p>

                <div className="onboarding-review-grid">
                  <article>
                    <h2>Client and business</h2>
                    <dl>
                      <div><dt>Name</dt><dd>{clientName}</dd></div>
                      <div><dt>Email</dt><dd>{formData.email}</dd></div>
                      <div><dt>Business</dt><dd>{formData.businessName}</dd></div>
                      <div><dt>Industry</dt><dd>{formData.industry}</dd></div>
                      <div><dt>Location</dt><dd>{formData.location}</dd></div>
                    </dl>
                  </article>
                  <article>
                    <h2>Current position</h2>
                    <dl>
                      <div><dt>Stage</dt><dd>{formData.businessStage}</dd></div>
                      <div><dt>Time in business</dt><dd>{formData.yearsInBusiness}</dd></div>
                      <div><dt>Team</dt><dd>{formData.teamSize}</dd></div>
                    </dl>
                  </article>
                  <article className="onboarding-review-wide">
                    <h2>Objectives</h2>
                    <p>{formData.objectives.join(' • ')}</p>
                    <dl>
                      <div><dt>Top priority</dt><dd>{formData.primaryObjective}</dd></div>
                      <div><dt>Timeframe</dt><dd>{formData.targetTimeframe}</dd></div>
                      <div><dt>Desired outcome</dt><dd>{formData.desiredOutcomes}</dd></div>
                    </dl>
                  </article>
                </div>

                <div className="onboarding-confirmation-box">
                  <h2>Service acknowledgment</h2>
                  <label className="onboarding-confirmation">
                    <input
                      type="checkbox"
                      name="informationConsent"
                      checked={formData.informationConsent}
                      onChange={updateField}
                      aria-invalid={Boolean(errors.informationConsent)}
                      aria-describedby={errors.informationConsent ? 'informationConsent-error' : undefined}
                    />
                    <span>
                      I authorize Selenne Strategies to use this information to evaluate my needs and prepare a consulting agreement.
                    </span>
                  </label>
                  <FieldError id="informationConsent-error">{errors.informationConsent}</FieldError>

                  <label className="onboarding-confirmation">
                    <input
                      type="checkbox"
                      name="serviceAcknowledgement"
                      checked={formData.serviceAcknowledgement}
                      onChange={updateField}
                      aria-invalid={Boolean(errors.serviceAcknowledgement)}
                      aria-describedby={errors.serviceAcknowledgement ? 'serviceAcknowledgement-error' : undefined}
                    />
                    <span>
                      I understand that, if approved, Business Consulting and Strategy is $200 per month and includes the Business Complete Subscription at selenne.io. This application is not the final service agreement and does not collect payment.
                    </span>
                  </label>
                  <FieldError id="serviceAcknowledgement-error">{errors.serviceAcknowledgement}</FieldError>
                </div>
              </fieldset>
            )}

            {submitError && (
              <div className="onboarding-submit-error" role="alert">
                <strong>We could not complete your submission.</strong>
                <span>{submitError}</span>
              </div>
            )}

            <div className="onboarding-actions">
              {currentStep > 0 && (
                <button type="button" className="onboarding-secondary-button" onClick={goToPreviousStep}>
                  Back
                </button>
              )}
              <button type="submit" className="onboarding-primary-button" disabled={isSubmitting}>
                {currentStep === steps.length - 1
                  ? (isSubmitting ? 'Submitting…' : 'Submit assessment')
                  : 'Continue'}
                {!isSubmitting && <span aria-hidden="true">→</span>}
              </button>
            </div>
          </form>
        </section>

        <aside className="onboarding-plan-card" aria-labelledby="onboarding-plan-title">
          <p className="onboarding-eyebrow">Proposed service</p>
          <h2 id="onboarding-plan-title">Business Consulting and Strategy</h2>
          <div className="onboarding-price">
            <strong>$200</strong>
            <span>per month</span>
          </div>
          <p>
            Practical guidance, structure, and accountability shaped around your business needs and objectives.
          </p>
          <div className="onboarding-included">
            <span aria-hidden="true">✓</span>
            <div>
              <strong>Business Complete included</strong>
              <p>Your monthly service includes access to the Business Complete Subscription at selenne.io.</p>
            </div>
          </div>
          <ul>
            <li>Business position and needs review</li>
            <li>Prioritized strategy and goals</li>
            <li>Customized service agreement</li>
            <li>Ongoing consulting support</li>
          </ul>
          <p className="onboarding-plan-note">
            You will not be charged on this page. Submission does not guarantee acceptance. Approved applicants receive a customized agreement before service begins.
          </p>
        </aside>
      </div>

      <footer className="onboarding-footer">
        <p>© {new Date().getFullYear()} Selenne Strategies. Your information is used only for application review and service planning.</p>
      </footer>
    </main>
  );
}

export default OnboardingPage;
